import { useState, useEffect } from 'react';
import { personalizationService, PersonalizationSettings } from '../services/personalizationService';

export interface BrandColors {
  // Primary brand colors
  primary: string;
  secondary: string;
  accent: string;
  
  // Text colors
  textPrimary: string;
  textSecondary: string;
  
  // Background colors
  background: string;
  surface: string;
  
  // State colors (keep semantic meaning)
  success: string;
  warning: string;
  error: string;
  info: string;
  
  // Neutral colors
  border: string;
  divider: string;
  disabled: string;
}

export const useBrandColors = (): BrandColors => {
  const [personalizationSettings, setPersonalizationSettings] = useState<PersonalizationSettings | null>(null);

  useEffect(() => {
    const loadPersonalization = async () => {
      try {
        const settings = await personalizationService.getPersonalizationSettings();
        setPersonalizationSettings(settings);
      } catch (error) {
        console.error('Failed to load personalization settings:', error);
      }
    };

    loadPersonalization();

    // Listen for personalization updates
    const handlePersonalizationUpdate = (event: CustomEvent<PersonalizationSettings>) => {
      setPersonalizationSettings(event.detail);
    };

    window.addEventListener('personalization-updated' as any, handlePersonalizationUpdate);
    
    return () => {
      window.removeEventListener('personalization-updated' as any, handlePersonalizationUpdate);
    };
  }, []);

  // Return brand-adapted colors with intelligent fallbacks
  return {
    // Primary brand colors
    primary: personalizationSettings?.theme.primaryColor || '#64748b',
    secondary: personalizationSettings?.theme.secondaryColor || '#94a3b8',
    accent: personalizationSettings?.theme.accentColor || '#f59e0b',
    
    // Text colors
    textPrimary: personalizationSettings?.theme.textColor || '#1a1a1a',
    textSecondary: '#666666', // Keep neutral for readability
    
    // Background colors
    background: personalizationSettings?.theme.backgroundColor || '#ffffff',
    surface: personalizationSettings?.theme.surfaceColor || '#f8f9fa',
    
    // State colors (keep semantic meaning but allow brand influence)
    success: '#27ae60', // Keep green for success
    warning: personalizationSettings?.theme.accentColor || '#f39c12', // Use brand accent or orange
    error: '#e74c3c', // Keep red for errors
    info: personalizationSettings?.theme.primaryColor || '#3498db', // Use brand primary or blue
    
    // Neutral colors (keep for accessibility)
    border: '#e9ecef',
    divider: '#e2e8f0',
    disabled: '#94a3b8',
  };
};
